import { z } from 'zod';
import { emailSchema, passwordSchema, usernameSchema } from './auth.js';
import { isoDateSchema, paginationQuerySchema } from './common.js';

export const adminUserSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  email: z.string(),
  username: z.string(),
  isAdmin: z.boolean(),
  emailVerified: z.boolean(),
  twoFactorEnabled: z.boolean(),
  suspended: z.boolean(),
  suspendedAt: isoDateSchema.nullable(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const adminUserListQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(254).optional(),
  filter: z.enum(['all', 'admins', 'suspended']).default('all'),
});

export const adminCreateUserSchema = z.object({
  email: emailSchema,
  username: usernameSchema,
  /** Omit to email the user a link to choose their own password. */
  password: passwordSchema.optional(),
  isAdmin: z.boolean().default(false),
});

export const adminUpdateUserSchema = z.object({
  email: emailSchema.optional(),
  username: usernameSchema.optional(),
  password: passwordSchema.optional(),
  isAdmin: z.boolean().optional(),
  /** Mark the email verified without sending a link. */
  emailVerified: z.boolean().optional(),
  /** Remove the user's 2FA (e.g. lost device). */
  disableTwoFactor: z.literal(true).optional(),
});

export const panelSettingsSchema = z.object({
  name: z.string().trim().min(1).max(64),
  registrationEnabled: z.boolean(),
  requireAdminTwoFactor: z.boolean(),
});
export type PanelSettings = z.infer<typeof panelSettingsSchema>;

export const updatePanelSettingsSchema = panelSettingsSchema.partial();

// ---- Backup storage ------------------------------------------------------------------------

export const backupSettingsSchema = z.object({
  /** Where new backups go. */
  disk: z.enum(['local', 's3']),
  /** When a server is at its backup limit, delete its oldest unlocked backup to make room. */
  rotateOldest: z.boolean(),
  s3: z.object({
    endpoint: z.string(),
    region: z.string(),
    bucket: z.string(),
    accessKey: z.string(),
    /** The secret is never sent back; this says whether one is stored. */
    secretKeySet: z.boolean(),
    pathStyle: z.boolean(),
    /** Folder inside the bucket, without slashes at the ends. */
    prefix: z.string(),
  }),
});
export type BackupSettings = z.infer<typeof backupSettingsSchema>;

export const updateBackupSettingsSchema = z.object({
  disk: z.enum(['local', 's3']),
  rotateOldest: z.boolean(),
  s3: z.object({
    endpoint: z.string().trim().url().max(500).or(z.literal('')),
    region: z.string().trim().max(64),
    bucket: z.string().trim().max(255),
    accessKey: z.string().trim().max(255),
    /** Omit to keep the stored secret. */
    secretKey: z.string().max(500).optional(),
    pathStyle: z.boolean(),
    prefix: z
      .string()
      .trim()
      .max(255)
      .transform((p) => p.replace(/^\/+|\/+$/g, '')),
  }),
});
